import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { updateAnalyticsDuration } from "store/actions/analytics";
import { fetchCIPData } from "store/thunk/analytics";

import getLogger from "utils/logger.util";
import { AnalyticsStrings } from "constants/constantStrings";
import { timestampInDefaultFormat } from "utils/utils";

import BadgeLabel from "components/BadgeLabel";
import DataDownloadDialog from "components/DataDownloadDialog";

import { AnalyticsTags } from "../AnalyticsHelper/Analytics.const";
import AnalyticsDataDownloader from "../AnalyticsHelper/AnalyticsDataDownloader";
import { parseAnalyticsData } from "../AnalyticsHelper/AnalyticsDataParser";
import AnalyticsActions from "store/actions/analytics";
import AnalyticsCIPOverlayPlot from "./AnalyticsCIPOverlayPlot";

const AnalyticsCIPOverlay = ({ xAxisRange, setXAxisRange }) => {
  const Logger = getLogger("AnalyticsCIPOverlay");
  const { tagsAsObj } = useSelector(state => state.tags);
  const dispatch = useDispatch();
  const { Duration, TagsWithoutData, PlotData, DurationRanges, Alarms, TagDefaultConfig } = useSelector(state => state.analyticsData);
  const [isChartData, setIsChartData] = useState(false);
  const [plotlyData, setPlotlyData] = useState();
  const [isDialogOpened, setIsDialogOpened] = useState(false);
  const [tagsWithData, setTagsWithData] = useState([]);
  const spike = useSelector(state => state.analyticsData.spike);

  const onToggleSpikeLine = () => {
    const newSpikeLineState = !spike;
    dispatch(AnalyticsActions.resetSpikeLine(newSpikeLineState));
    dispatch(fetchCIPData(newSpikeLineState));
    // Reset xAxisRange to full range using DurationRanges when zoomed in and clicked on toggle
    if (DurationRanges && DurationRanges?.startTime && DurationRanges?.endTime) {
        setXAxisRange([timestampInDefaultFormat(DurationRanges.startTime), timestampInDefaultFormat(DurationRanges.endTime)]);
    }
  };
  
  const getTagsWithData = () => {
    const tagsWithData = [];
    const tagIDsWithoutData = TagsWithoutData.map(({ TagId }) => TagId);
    AnalyticsTags.forEach(tag => {
      const { TagId } = tag;
      if (!tagIDsWithoutData.includes(TagId)) {
        tag = { ...tag, ...tagsAsObj[TagId] };
        const Data = PlotData.Data[TagId] || [];
        const Baseline = PlotData.Baselines[TagId];
        tagsWithData.push({ ...tag, Data, Baseline, Type: TagDefaultConfig.Type });
      }
    });
    return tagsWithData;
  };

  useEffect(() => {
    if (PlotData && TagsWithoutData.length !== Object.keys(PlotData.Data).length) {
      const TagsWithData = getTagsWithData();
      setTagsWithData(TagsWithData);
    }
  }, [PlotData]);

  useEffect(() => {
    if (tagsWithData.length) {
      setIsChartData(true);
      const parsedData = parseAnalyticsData(tagsWithData, PlotData, Alarms, xAxisRange);
      Logger.log("parsedData: ", tagsWithData, parsedData, xAxisRange);
      setPlotlyData(parsedData);
    } else {
      setIsChartData(false);
    }
  }, [tagsWithData, Alarms, xAxisRange]);

  const onPlotRefresh = () => {
    dispatch(updateAnalyticsDuration({ Duration, DurationRanges }));
    dispatch(AnalyticsActions.resetSpikeLine(false));
    dispatch(fetchCIPData());
    if (DurationRanges && DurationRanges?.startTime && DurationRanges?.endTime) {
        setXAxisRange([timestampInDefaultFormat(DurationRanges.startTime), timestampInDefaultFormat(DurationRanges.endTime)]);
    }
  };
  const onDownloadPlot = () => setIsDialogOpened(true);

  const onPlotZoom = zoomEvent => {
    if (zoomEvent && zoomEvent.dragmode !== "zoom" && !zoomEvent.autosize) {
      const xAxisChange = Object.fromEntries(Object.entries(zoomEvent).filter(([key]) => key.includes("xaxis")));
      const xRange = Object.values(xAxisChange);
      setXAxisRange(xRange);
    }
  };

  const onDialogClose = (data, downloadDurationMsg) => {
    if (data) {
      const { interval } = data;
      if (interval) {
        AnalyticsDataDownloader.fetchAndDownloadCIPData(interval, DurationRanges, downloadDurationMsg, tagsAsObj);
      } else {
        AnalyticsDataDownloader.downloadCipData(PlotData, downloadDurationMsg, tagsAsObj);
      }
    }
    setIsDialogOpened(false);
  };

  return (
    <>
      <div className="analytics-cip">
        <BadgeLabel badgeDescription={AnalyticsStrings.CIP_Overlay} />
        {isChartData && plotlyData ? (
          <AnalyticsCIPOverlayPlot 
            {...plotlyData} 
            onPlotRefresh={onPlotRefresh} 
            onDownloadPlot={onDownloadPlot} 
            onPlotZoom={onPlotZoom}
            onToggleSpikeLine={onToggleSpikeLine} 
            isSpikeLineActive={spike} />
        ) : (
          <label className="dupont-form-label">{AnalyticsStrings.noDataMsg}</label>
        )}
      </div>
      {isDialogOpened && <DataDownloadDialog DurationRanges={DurationRanges} onDialogClose={onDialogClose} />}
    </>
  );
};

export default AnalyticsCIPOverlay;
